import { useCallback, useEffect, useRef, useState } from 'react'

/** Fansly takes these (the Fansly workspace's order: mp4 first, as Safari and Fansly's own client record). */
const MIME_PREFERENCE = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
/** A recording stops by itself after 5 minutes. */
export const MAX_RECORDING_MS = 5 * 60_000
/** Shorter than this is a slip of the finger, not a voice note. */
export const MIN_RECORDING_MS = 400

export interface VoiceNote {
  file: File
  /** A local URL to listen back before sending. */
  url: string
  seconds: number
}

export type RecordFailure = 'unsupported' | 'denied' | 'tooShort'

function pickMime(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  return MIME_PREFERENCE.find((m) => MediaRecorder.isTypeSupported?.(m)) ?? ''
}

function extensionOf(mime: string): string {
  if (mime.includes('mp4')) return 'm4a'
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('mpeg')) return 'mp3'
  if (mime.includes('wav')) return 'wav'
  return 'webm'
}

/**
 * Records a voice note from the microphone (MediaRecorder, 250 ms slices).
 * One recording at a time; unmounting drops it and releases the microphone.
 */
export function useVoiceRecorder(onDone: (note: VoiceNote | null, failure?: RecordFailure) => void) {
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const rec = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const startedAt = useRef(0)
  const keep = useRef(true)
  const stopTimer = useRef<number | undefined>(undefined)
  const done = useRef(onDone)
  done.current = onDone

  const release = useCallback(() => {
    rec.current?.stream.getTracks().forEach((t) => t.stop())
    rec.current = null
    window.clearTimeout(stopTimer.current)
    setRecording(false)
  }, [])

  useEffect(() => {
    if (!recording) return
    const id = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt.current) / 1000)), 250)
    return () => window.clearInterval(id)
  }, [recording])

  useEffect(
    () => () => {
      keep.current = false
      if (rec.current?.state === 'recording') rec.current.stop()
      rec.current?.stream.getTracks().forEach((t) => t.stop())
      window.clearTimeout(stopTimer.current)
    },
    [],
  )

  const start = useCallback(async () => {
    if (rec.current) return
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return done.current(null, 'unsupported')
    let media: MediaStream
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      return done.current(null, 'denied')
    }
    const mime = pickMime()
    const recorder = new MediaRecorder(media, mime ? { mimeType: mime } : undefined)
    chunks.current = []
    keep.current = true
    recorder.addEventListener('dataavailable', (e) => e.data?.size && chunks.current.push(e.data))
    recorder.addEventListener('stop', () => {
      const ms = Date.now() - startedAt.current
      const type = recorder.mimeType || mime || 'audio/webm'
      const blob = new Blob(chunks.current, { type })
      release()
      if (!keep.current) return
      if (ms < MIN_RECORDING_MS || !blob.size) return done.current(null, 'tooShort')
      const file = new File([blob], `fansly-audio-recording.${extensionOf(type)}`, { type: type.split(';')[0] })
      done.current({ file, url: URL.createObjectURL(blob), seconds: Math.max(1, Math.round(ms / 1000)) })
    })
    rec.current = recorder
    startedAt.current = Date.now()
    setSeconds(0)
    recorder.start(250)
    setRecording(true)
    stopTimer.current = window.setTimeout(() => recorder.state === 'recording' && recorder.stop(), MAX_RECORDING_MS)
  }, [release])

  /** Stop and keep the recording. */
  const stop = useCallback(() => {
    keep.current = true
    if (rec.current?.state === 'recording') rec.current.stop()
  }, [])

  /** Stop and throw it away. */
  const cancel = useCallback(() => {
    keep.current = false
    if (rec.current?.state === 'recording') rec.current.stop()
    else release()
  }, [release])

  return { recording, seconds, start, stop, cancel }
}
